import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ModalsState {
  newWeek: boolean;
  welcome: boolean;
  upgrade: boolean;
  calendarSelection: boolean;
  upgradeFeature?: string;
}

interface ModalsContextType {
  modals: ModalsState;
  openModal: (modal: keyof ModalsState, feature?: string) => void;
  closeModal: (modal: keyof ModalsState) => void;
}

const ModalsContext = createContext<ModalsContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useModals = () => {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useModals must be used within ModalsProvider');
  }
  return context;
};

interface ModalsProviderProps {
  children: ReactNode;
}

export const ModalsProvider: React.FC<ModalsProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<ModalsState>({
    newWeek: false,
    welcome: false,
    upgrade: false,
    calendarSelection: false });

  const openModal = (modal: keyof ModalsState, feature?: string) => {
    setModals(prev => ({ 
      ...prev, 
      [modal]: true,
      ...(feature && { upgradeFeature: feature })
    }));
  };

  const closeModal = (modal: keyof ModalsState) => {
    setModals(prev => ({ 
      ...prev, 
      [modal]: false,
      ...(modal === 'upgrade' && { upgradeFeature: undefined })
    }));
  };

  return (
    <ModalsContext.Provider value={{ modals, openModal, closeModal }}>
      {children}
    </ModalsContext.Provider>
  );
};
